import type React from 'react';

import { scaleForStrength } from '../../matrix-cell-scale.ts';

import { useTooltipHandlers, type TooltipContent } from './Tooltip.tsx';

interface MatrixCellProps {
  /**
   * Display value. `null` renders an empty (na) cell; numeric values render
   * with the appropriate level class.
   */
  value: number | null;
  /** CSS class string for the cell (e.g. "lvl-3", "v0", "teal lvl-2"). */
  levelClass: string;
  /** Extra classes (e.g. "is-merged"). */
  extraClass?: string;
  /**
   * How much of this derivedContext's evidence this business function carries, 0..1,
   * relative to the derivedContext's own total.
   *
   * Only drawn when `scaleByStrength` is on (the debug magnitude view). The
   * default cell shows no magnitude at all: an always-on bar was tried and read
   * as decoration — too small to compare, too busy to ignore. Colour stays
   * reserved for the talent level, since overloading it would make a strong cell
   * at a low level indistinguishable from a weak cell at a high one.
   */
  strength?: number;
  /**
   * DEBUG: shrink the cell toward its centre in proportion to `strength`. Off by
   * default — a grid of equal boxes scans better — but relative size is the
   * fastest way to sanity-check the weighting.
   */
  scaleByStrength?: boolean;
  tooltip?: TooltipContent | null;
}

export function MatrixCell({
  value,
  levelClass,
  extraClass,
  tooltip,
  strength,
  scaleByStrength = false,
}: MatrixCellProps) {
  const handlers = useTooltipHandlers(tooltip ?? null);
  const scale = scaleByStrength ? scaleForStrength(strength) : null;
  const className = ['snap-cell', levelClass, extraClass, scale === null ? '' : 'is-scaled']
    .filter(Boolean)
    .join(' ');
  return (
    <div
      className={className}
      {...handlers}
      {...(scale === null ? {} : { style: { '--cell-scale': scale } as React.CSSProperties })}
    >
      {value === null ? '' : String(value)}
    </div>
  );
}
